
import { useState } from 'react';
import { useDepartments, useAddDepartment } from '@/hooks/useDepartments';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus } from 'lucide-react';

interface DepartmentAssignmentSectionProps {
  selectedDepartments: string[];
  onDepartmentToggle: (departmentId: string) => void;
}

export const DepartmentAssignmentSection = ({ selectedDepartments, onDepartmentToggle }: DepartmentAssignmentSectionProps) => {
  const { data: departments, isLoading } = useDepartments();
  const addDepartmentMutation = useAddDepartment();
  const [newDepartment, setNewDepartment] = useState('');

  const handleAddDepartment = async () => {
    if (newDepartment.trim() === '') return;
    try {
      await addDepartmentMutation.mutateAsync(newDepartment.trim());
      setNewDepartment('');
    } catch (error) {
      console.error("Failed to add department", error);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">تعيين الأقسام</h3>
      <div className="space-y-2">
        <Label>الأقسام المتاحة</Label>
        <div className="flex flex-wrap gap-2">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-6 w-20 rounded-full" />)
          ) : (
            departments?.map((dep) => {
              const isSelected = selectedDepartments.includes(dep.id);
              return (
                <Badge
                  key={dep.id}
                  onClick={() => onDepartmentToggle(dep.id)}
                  variant={isSelected ? 'default' : 'secondary'}
                  className="cursor-pointer transition-all"
                >
                  {dep.name}
                </Badge>
              );
            })
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="new-department">إضافة قسم جديد</Label>
        <div className="flex gap-2">
          <Input
            id="new-department"
            value={newDepartment}
            onChange={(e) => setNewDepartment(e.target.value)}
            placeholder="اسم القسم الجديد"
            disabled={addDepartmentMutation.isPending}
          />
          <Button 
            type="button" 
            onClick={handleAddDepartment} 
            disabled={addDepartmentMutation.isPending || !newDepartment.trim()}
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة
          </Button>
        </div>
      </div>
    </div>
  );
};
